import { queryClient } from '@common/config/api/queryClient'
import { createContext, ReactNode, useCallback, useContext, useRef } from 'react'

export const STUDY_AND_SERIES_SECTION_SELECTOR = '[data-study-and-series-section]'

type FlushHandler = () => Promise<boolean>

type StudyAndSeriesFlushContextType = {
  registerFlush: (id: string, flush: FlushHandler) => void
  unregisterFlush: (id: string) => void
  registerScrollToSection: (scrollFn: () => void) => void
  scrollToStudyAndSeriesSection: () => void
  hasRegisteredStudyDefinitions: () => boolean
  flushAllStudyAndSeriesDefinitions: () => Promise<boolean>
}

const StudyAndSeriesFlushContext = createContext<StudyAndSeriesFlushContextType | undefined>(undefined)

export function StudyAndSeriesFlushProvider({ children }: { children: ReactNode }) {
  const flushHandlersRef = useRef<Map<string, FlushHandler>>(new Map())
  const scrollToSectionRef = useRef<(() => void) | null>(null)

  const registerFlush = useCallback((id: string, flush: FlushHandler) => {
    flushHandlersRef.current.set(id, flush)
  }, [])

  const unregisterFlush = useCallback((id: string) => {
    flushHandlersRef.current.delete(id)
  }, [])

  const registerScrollToSection = useCallback((scrollFn: () => void) => {
    scrollToSectionRef.current = scrollFn
  }, [])

  const scrollToStudyAndSeriesSection = useCallback(() => {
    scrollToSectionRef.current?.()
  }, [])

  const hasRegisteredStudyDefinitions = useCallback(() => flushHandlersRef.current.size > 0, [])

  const flushAllStudyAndSeriesDefinitions = useCallback(async () => {
    for (const flush of flushHandlersRef.current.values()) {
      const ok = await flush()
      if (!ok) {
        scrollToSectionRef.current?.()
        await new Promise((resolve) => setTimeout(resolve, 100))
        document
          .querySelector(`${STUDY_AND_SERIES_SECTION_SELECTOR} .Mui-error`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return false
      }
    }

    await queryClient.invalidateQueries({ queryKey: ['QCTask'] })
    return true
  }, [])

  return (
    <StudyAndSeriesFlushContext.Provider
      value={{
        registerFlush,
        unregisterFlush,
        registerScrollToSection,
        scrollToStudyAndSeriesSection,
        hasRegisteredStudyDefinitions,
        flushAllStudyAndSeriesDefinitions,
      }}
    >
      {children}
    </StudyAndSeriesFlushContext.Provider>
  )
}

export function useStudyAndSeriesFlush() {
  const context = useContext(StudyAndSeriesFlushContext)
  if (!context) {
    throw new Error('useStudyAndSeriesFlush must be used within StudyAndSeriesFlushProvider')
  }
  return context
}
